import { OnboardingListConfig, OnboardingListTypeKey } from "./onboardingListTypes";

/** A list the Lists step already created, and the config it was created from. */
export interface CreatedOnboardingList {
	listId: number;
	configHash: string;
	/**
	 * The criteria the list was created from. Kept so a later step can reuse them
	 * — the Track step seeds its people search from the lead-database / lookalike
	 * list the user just built (PREC-3719). Optional: entries written before this
	 * existed carry only the hash.
	 */
	config?: OnboardingListConfig;
}

export type CreatedOnboardingLists = Partial<Record<OnboardingListTypeKey, CreatedOnboardingList>>;

const STORAGE_PREFIX = "onboarding-created-lists";

const storageKey = (userId: number): string => `${STORAGE_PREFIX}:${userId}`;

const isCreatedList = (value: unknown): value is CreatedOnboardingList => {
	const entry = value as CreatedOnboardingList | null;

	return (
		typeof entry === "object" &&
		entry !== null &&
		typeof entry.listId === "number" &&
		typeof entry.configHash === "string"
	);
};

/** Sorts object keys at every level so two equal configs always hash equally. */
const stableValue = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value.map(stableValue);
	}

	if (typeof value === "object" && value !== null) {
		return Object.keys(value as Record<string, unknown>)
			.sort()
			.reduce<Record<string, unknown>>((acc, key) => {
				acc[key] = stableValue((value as Record<string, unknown>)[key]);

				return acc;
			}, {});
	}

	return value;
};

/**
 * Fingerprints a picked config so a repeat visit to the Lists step can tell
 * "same list, don't touch it" from "the user edited it, replace it".
 */
export const hashConfig = (config: OnboardingListConfig): string =>
	JSON.stringify(stableValue(config));

/**
 * What the Lists step created for this user so far. Kept in `localStorage`
 * rather than component state so it survives the page reload that an
 * interrupted onboarding resumes from (PREC-3719).
 */
export const readCreatedLists = (userId: number): CreatedOnboardingLists => {
	try {
		const raw = window.localStorage.getItem(storageKey(userId));

		if (!raw) {
			return {};
		}

		const parsed: unknown = JSON.parse(raw);

		if (typeof parsed !== "object" || parsed === null) {
			return {};
		}

		return Object.entries(parsed as Record<string, unknown>).reduce<CreatedOnboardingLists>(
			(acc, [key, value]) => {
				if (isCreatedList(value)) {
					acc[key as OnboardingListTypeKey] = value;
				}

				return acc;
			},
			{}
		);
	} catch (err) {
		console.error(err);

		return {};
	}
};

export const writeCreatedLists = (userId: number, created: CreatedOnboardingLists): void => {
	try {
		window.localStorage.setItem(storageKey(userId), JSON.stringify(created));
	} catch (err) {
		console.error(err);
	}
};
